/**
 * DV-002/DV-003: Demo Selection Hook
 * URL-driven demo page selection with query param support and session persistence
 */

import { useCallback, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface DemoPage {
  id: string;
  name: string;
  description: string;
}

export const DEMO_PAGES: readonly DemoPage[] = [
  { id: 'websockets', name: 'WebSockets/Realtime', description: 'Connection lifecycle, token issuance, events' },
  { id: 'temporal', name: 'Temporal/Workflow', description: 'Events v2 timeline viewer with filtering' },
  { id: 'heartbeats', name: 'Heartbeats/QC', description: 'Live stage heartbeats and QC summary' },
  { id: 'stage-tester', name: 'Stage Tester', description: 'Run pipeline stages in isolation with controllable inputs, prompt preview, and high-definition logging' },
  { id: 'feature-flags', name: 'Feature Flags', description: 'Admin console for feature flags management with toggles and audit' },
  { id: 'stage-outputs', name: 'Stage Outputs', description: 'Preview artifacts from stage outputs with URL verification and CORS handling' },
  { id: 'auth', name: 'Auth/PKCE', description: 'Cookie/session diagnostics, echo endpoints' },
  { id: 'billing', name: 'Billing/Commits', description: 'Admin-only billing commits inspector with cost tracking and API call details' },
] as const;

export type DemoPageId = typeof DEMO_PAGES[number]['id'];

const SESSION_STORAGE_KEY = 'demo_viewer_page';

// Session storage utilities
const getSessionPage = (): DemoPageId | null => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored && DEMO_PAGES.find(page => page.id === stored)) {
      return stored as DemoPageId;
    }
  } catch (error) {
    // Session storage might not be available (SSR, incognito, etc.)
    console.warn('Session storage not available:', error);
  }
  return null;
};

const setSessionPage = (pageId: DemoPageId): void => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, pageId);
  } catch (error) {
    // Fail silently if session storage is not available
    console.warn('Could not save to session storage:', error);
  }
};

export function useDemoSelection() {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('🔄 useDemoSelection: Hook called');
  }
  const [searchParams, setSearchParams] = useSearchParams();
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('🔄 useDemoSelection: searchParams:', Object.fromEntries(searchParams.entries()));
  }

  // Stabilize dependency by stringifying params (react-router returns a new object identity frequently)
  const searchKey = useMemo(() => searchParams.toString(), [searchParams]);

  // Get current page from URL, with session storage fallback, then default
  const currentPageId = useMemo(() => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('🔄 useDemoSelection: currentPageId useMemo computing...');
    }
    // Priority 1: URL parameter
    const pageParam = new URLSearchParams(searchKey).get('page');
    const validUrlPage = DEMO_PAGES.find(page => page.id === pageParam);
    if (validUrlPage) {
      return validUrlPage.id;
    }

    // Priority 2: Session storage
    const sessionPage = getSessionPage();
    if (sessionPage) {
      return sessionPage;
    }

    // Priority 3: Default to first page
    const result = DEMO_PAGES[0].id;
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('🔄 useDemoSelection: currentPageId result:', result);
    }
    return result;
  }, [searchKey]);

  const currentPage = useMemo(() => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('🔄 useDemoSelection: currentPage useMemo computing for pageId:', currentPageId);
    }
    const result = DEMO_PAGES.find(page => page.id === currentPageId) || DEMO_PAGES[0];
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('🔄 useDemoSelection: currentPage result:', result.id, result.name);
    }
    return result;
  }, [currentPageId]);

  // Sync current page to session storage whenever it changes
  useEffect(() => {
    setSessionPage(currentPageId);
  }, [currentPageId]);

  // Set page and update URL
  const setPage = useCallback((pageId: DemoPageId) => {
    // No-op if already on the requested page
    if (pageId === currentPageId) return;

    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (pageId === DEMO_PAGES[0].id) {
        // Remove page param for default page to keep URLs clean
        newParams.delete('page');
      } else {
        newParams.set('page', pageId);
      }
      // Only update if params actually changed
      if (newParams.toString() === prev.toString()) {
        return prev;
      }
      return newParams;
    });
    // Session storage will be updated by the useEffect above
  }, [currentPageId, setSearchParams]);

  // Step through pages (for keyboard navigation) with cycling
  const stepPage = useCallback((direction: 1 | -1, options?: { replace?: boolean }) => {
    const currentIndex = DEMO_PAGES.findIndex(page => page.id === currentPageId);
    if (currentIndex === -1) return;

    let newIndex = currentIndex + direction;

    // Wrap around at boundaries for cycling
    if (newIndex < 0) {
      newIndex = DEMO_PAGES.length - 1;
    } else if (newIndex >= DEMO_PAGES.length) {
      newIndex = 0;
    }

    const newPageId = DEMO_PAGES[newIndex].id;

    // Support history replace for rapid navigation
    if (options?.replace) {
      if (newPageId === currentPageId) return; // avoid no-op replace churn
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        if (newPageId === DEMO_PAGES[0].id) {
          newParams.delete('page');
        } else {
          newParams.set('page', newPageId);
        }
        if (newParams.toString() === prev.toString()) {
          return prev;
        }
        return newParams;
      }, { replace: true });
    } else {
      setPage(newPageId);
    }
  }, [currentPageId, setPage, setSearchParams]);

  return {
    currentPage,
    currentPageId,
    setPage,
    stepPage,
    availablePages: DEMO_PAGES,
  };
}